import { homePage } from './pages/home';
import { projectsPage } from './pages/projects';
import { projectDetailPage } from './pages/projectDetail';
import { agrilubDetailPage } from './pages/agrilubDetail';
import { saintnectaireDetailPage } from './pages/saintnectaireDetail';
import { Chatbot } from './components/chatbot';
import { ThemeToggle } from './components/themeToggle';
import { initScrollAnimations } from './utils/animations';

interface Route {
  path: string;
  render: () => string;
}

function normalizeRouteSegment(segment: string): string {
  if (segment.startsWith('project')) {
    return 'projects';
  }
  return segment || 'home';
}

const routes: Route[] = [
  { path: 'home', render: homePage },
  { path: 'projects', render: projectsPage },
  { path: 'project/plan-de-travail', render: projectDetailPage },
  { path: 'project/agrilib', render: agrilubDetailPage },
  { path: 'project/saintnectaire', render: saintnectaireDetailPage },
];

async function router() {
  try {
    let hash = window.location.hash.slice(1) || 'home';
    if (hash.startsWith('/')) {
      hash = hash.slice(1);
    }

    const route = routes.find(r => r.path === hash) || routes[0];
    const app = document.getElementById('app');
    if (!app) {
      console.error('App container not found');
      return;
    }

    if (!app.classList.contains('page-transition')) {
      app.classList.add('page-transition');
    }

    // Fade out
    app.classList.add('fade-out');
    await new Promise(resolve => setTimeout(resolve, 250));

    // Update content
    app.innerHTML = route.render();

    // Scroll to top
    window.scrollTo(0, 0);

    // Fade in
    app.classList.remove('fade-out');

    // Move focus for accessibility (announce page change to screen readers)
    app.focus({ preventScroll: true });

    // Initialize scroll animations after DOM update
    setTimeout(() => {
      initScrollAnimations();
    }, 100);
  } catch (error) {
    console.error('Routing error:', error);
    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div class="min-h-[60vh] flex items-center justify-center">
          <div class="text-center container-narrow">
            <h1 class="font-display font-bold text-display-md mb-4" style="color: var(--text-primary)">Une erreur est survenue</h1>
            <p class="font-body text-body-md mb-6" style="color: var(--text-secondary)">La page demandee n'a pas pu etre chargee.</p>
            <a href="#home" class="btn-primary">Retour a l'accueil</a>
          </div>
        </div>
      `;
      app.classList.remove('fade-out');
    }
  }

  // Update active nav link
  document.querySelectorAll('.nav-link').forEach(link => {
    link.classList.remove('active');
    link.removeAttribute('aria-current');
    const rawHash = window.location.hash.slice(1) || 'home';
    const sanitizedHash = rawHash.startsWith('/') ? rawHash.slice(1) : rawHash;
    const normalizedBase = normalizeRouteSegment(sanitizedHash.split('/')[0] || sanitizedHash);
    const linkPage = link.getAttribute('data-page');
    if (linkPage === normalizedBase) {
      link.classList.add('active');
      link.setAttribute('aria-current', 'page');
    }
  });
}

// Handle navigation clicks
document.addEventListener('click', (e) => {
  const target = e.target;
  if (target instanceof HTMLElement && target.matches('.nav-link')) {
    e.preventDefault();
    const page = target.getAttribute('data-page');
    window.location.hash = page || 'home';
  }
});

// Listen to hash changes
window.addEventListener('hashchange', router);

// Navbar scroll behavior
let navbarIsScrolled = false;

function handleScroll() {
  const navbar = document.getElementById('navbar');
  if (!navbar) return;

  const shouldBeScrolled = window.scrollY > 50;
  if (shouldBeScrolled === navbarIsScrolled) return;

  navbar.classList.toggle('scrolled', shouldBeScrolled);
  navbarIsScrolled = shouldBeScrolled;
}

window.addEventListener('scroll', handleScroll, { passive: true });
handleScroll();

// Initial load
router();

// Initialize components
new ThemeToggle();
new Chatbot();
